import { useTranslation } from 'react-i18next';
import { Target, ArrowDown } from 'lucide-react';

export default function NegotiationAssistant({ deal }) {
  const { t } = useTranslation();
  const ask = deal.purchase_price;
  const fair = deal.suggested_offer_price;
  const opening = Math.round(fair * 0.95);
  const savings = ask - fair;
  const pct = ((savings / ask) * 100).toFixed(1);

  const rangeSpan = ask - opening;
  const fairPos = rangeSpan > 0 ? ((fair - opening) / rangeSpan) * 100 : 50;

  return (
    <div data-testid="negotiation-assistant" className="bg-white dark:bg-[#0F1419] border border-slate-200 dark:border-white/[0.06] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Target className="w-4 h-4 text-blue-500" />
        <h3 className="font-semibold text-sm text-slate-900 dark:text-white">{t('decision.negotiation')}</h3>
      </div>

      <div className="space-y-2.5 mb-4">
        <Row label={t('decision.currentAsking')} value={`$${ask.toLocaleString()}`} cls="text-slate-900 dark:text-white" />
        <Row label={t('decision.estimatedFairValue')} value={`$${fair.toLocaleString()}`} cls="text-emerald-500" />
        <Row label={t('decision.openingOffer')} value={`$${opening.toLocaleString()}`} cls="text-blue-500" />
        <Row label={t('decision.maxOffer')} value={`$${fair.toLocaleString()}`} cls="text-amber-500" />
      </div>

      <div className="mb-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{t('decision.negotiationRange')}</p>
        <div className="relative h-2 bg-slate-100 dark:bg-white/[0.06] rounded-full">
          <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full"
            style={{ width: `${Math.min(100, fairPos)}%` }} />
          <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-[#0F1419] shadow"
            style={{ left: `calc(${Math.min(97, fairPos)}% - 6px)` }} />
        </div>
        <div className="flex justify-between mt-1 text-[10px] text-slate-400 tabular-nums">
          <span>${opening.toLocaleString()}</span>
          <span>${ask.toLocaleString()}</span>
        </div>
      </div>

      {savings > 0 && (
        <div className="flex items-center gap-2 bg-emerald-500/[0.08] border border-emerald-500/20 rounded-lg p-3">
          <ArrowDown className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          <span className="text-sm font-bold text-emerald-500">{t('decision.potentialSavings')}: ${savings.toLocaleString()}</span>
          <span className="text-xs text-slate-400">({pct}% {t('decision.below')})</span>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, cls }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className={`font-bold tabular-nums ${cls}`}>{value}</span>
    </div>
  );
}
